// src/layouts/HostLayout.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../utils/firebase';
import Logo from '../components/Logo';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import ChatToggleButton from '../components/ChatToggleButton';
import { hasActiveCreatorSubscription } from '../utils/stripeClient';


const HostLayout = ({ children, hideNavigation = false }) => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [userRole, setUserRole] = React.useState(null);
  const [hasCreatorAccess, setHasCreatorAccess] = React.useState(false);
  const [isCheckingCreatorAccess, setIsCheckingCreatorAccess] = React.useState(false);

  React.useEffect(() => {
    const fetchRoleAndCreatorAccess = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const role = userDoc.data().role || 'user';
            setUserRole(role);
            
            // Redirect admin users away from host layout
            if (role === 'admin') {
              navigate('/admin', { replace: true });
              return;
            }
            
            // Redirect team users to their dashboard
            if (role === 'team_captain' || role === 'team_member') {
              navigate('/team/dashboard', { replace: true });
              return;
            }
            
            // Check creator subscription access (only if not already checking and not cached)
            if (!isCheckingCreatorAccess && hasCreatorAccess === null) {
              setIsCheckingCreatorAccess(true);
              try {
                const creatorAccess = await hasActiveCreatorSubscription();
                setHasCreatorAccess(creatorAccess);
              } catch (error) {
                console.error('Error checking creator access:', error);
                setHasCreatorAccess(false);
              } finally {
                setIsCheckingCreatorAccess(false);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          // Don't redirect on error, just set default role
          setUserRole('user');
        }
      }
    };
    
    // Add a longer delay to prevent rapid successive calls
    const timeoutId = setTimeout(fetchRoleAndCreatorAccess, 500);
    return () => clearTimeout(timeoutId);
  }, [user, navigate, isCheckingCreatorAccess, hasCreatorAccess]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleHelpDesk = () => {
    if (userRole === 'admin') {
      navigate('/admin/support-tickets');
    } else {
      navigate('/helpdesk');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative">
      {/* Custom Host Nav - Hidden during active game */}
      {!hideNavigation && (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/home')}>
          <div className="flex items-center">
            <Logo size={40} className="mr-2 logo-white" />
            <span className="text-white text-xl font-bold tracking-wide">Host Dashboard</span>
          </div>
        </div>
        <div className="flex gap-6">
          <button onClick={() => navigate('/home')} className="text-green-200 hover:text-white font-semibold transition">Dashboard</button>
          <button onClick={() => navigate('/profile')} className="text-green-200 hover:text-white font-semibold transition">Profile</button>
          {hasCreatorAccess && (
            <button onClick={() => navigate('/my-games')} className="text-green-200 hover:text-white font-semibold transition">My Games</button>
          )}
          <button onClick={() => navigate('/marketplace')} className="text-green-200 hover:text-white font-semibold transition">Marketplace</button>
          <button onClick={handleHelpDesk} className="text-green-200 hover:text-white font-semibold transition">Help Desk</button>
          <button onClick={handleLogout} className="text-red-400 hover:text-white font-semibold transition">Log Out</button>
        </div>
      </nav>
      )}
      <main className={`p-4 ${hideNavigation ? 'pt-4' : 'pt-32'}`}>{children}</main>
      <ChatToggleButton />
      <style>{`
        .logo-white {
          filter: brightness(0) invert(1) drop-shadow(0 0 16px #fff8);
        }
      `}</style>
    </div>
  );
};

export default HostLayout;
